/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import type { Column } from '@features/property-management/components/DataTable';
import type { KanbanVisit } from '@pages/authenticated/agency-pages/visits/useVisitsKanban';
import { VisitStatusBadge } from '../components/VisitStatusBadge';
import { Text } from '@/shared/ui';

type Translate = (key: string) => string;

/**
 * Builds the table column definitions for the visits DataTable.
 *
 * @param t - The translation function scoped to the visits namespace.
 * @param options - Set `showBroker: false` for solo-broker agencies, where the
 *   column would repeat the same single broker on every row.
 * @returns The ordered list of column definitions.
 */
export function buildVisitTableColumns(
  t: Translate,
  options: { showBroker?: boolean } = {},
): Column<KanbanVisit>[] {
  const { showBroker = true } = options;
  const columns: Column<KanbanVisit>[] = [
    {
      key: 'property', label: t('visits.table.headers.property'), width: '22%', sortable: true,
      render: (v) => (
        <div>
          <Text
            as="span"
            style={{
              fontWeight: 'var(--font-semibold)' as unknown as number,
              color: 'var(--text-primary)',
            }}
          >
            {v.property}
          </Text>
          {v.propertyAddress && (
            <div style={{ fontSize: 'var(--text-micro)', color: 'var(--text-tertiary)' }}>
              {v.propertyAddress}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'client', label: t('visits.table.headers.client'), width: '16%', sortable: true,
      render: (v) => <Text as="span">{v.client}</Text>,
    },
    {
      key: 'date', label: t('visits.table.headers.date'), width: '12%', sortable: true,
      render: (v) => <Text as="span">{v.date.split('-').reverse().join('/')}</Text>,
    },
    {
      key: 'time', label: t('visits.table.headers.time'), width: '10%',
      render: (v) => <Text as="span">{v.time}</Text>,
    },
    {
      key: 'broker', label: t('visits.table.headers.broker'), width: '16%', sortable: true,
      render: (v) => <Text as="span">{v.broker}</Text>,
    },
    {
      key: 'status', label: t('visits.table.headers.status'), width: '14%',
      render: (v) => <VisitStatusBadge status={v.status} />,
    },
  ];

  return showBroker ? columns : columns.filter((column) => column.key !== 'broker');
}

/**
 * Builds the period-filter chip options for the visits toolbar.
 *
 * @param t - The translation function scoped to the visits namespace.
 * @returns The list of period options with keys and localized labels.
 */
export function buildPeriodOptions(t: Translate): { key: string; label: string }[] {
  return [
    { key: 'today', label: t('visits.periods.today') },
    { key: 'thisWeek', label: t('visits.periods.thisWeek') },
    { key: 'thisMonth', label: t('visits.periods.thisMonth') },
  ];
}
